import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Preload } from "@react-three/drei";
import * as THREE from "three";

import EarthMesh        from "./EarthMesh";
import Atmosphere       from "./Atmosphere";
import LocationMarkers  from "./LocationMarkers";
import NetworkArcs      from "./NetworkArcs";
import OrbitRings       from "./OrbitRings";
import Particles        from "./Particles";
import { HERO_COLORS, latLngToVec3 }  from "./constants";
import useEarthAnimation from "@/hooks/useEarthAnimation";

/* ---------------------------- easing ------------------------------------ */
const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

/* ------------------------- animation constants -------------------------- */
const FOCUS_DURATION_MS = 1800;   // 1.5–2s per requirement
const PAUSE_AFTER_MS    = 5000;   // 5s hold on selection
const DOLLY_IN_FACTOR   = 0.86;   // camera zooms ~14% closer during focus

/**
 * Full 3D scene composition — everything lives inside <Canvas>.
 * Reusable: consumers just render <EarthScene className=".." onLocationChange={...} />
 *
 *  onLocationChange(location | null)  — fired whenever the active focus changes
 *      (location is the HERO_LOCATIONS entry, including its mocked `stats`).
 */
export default function EarthScene({ className = "", onLocationChange }) {
  const controlsRef = useRef();
  const [selectedId, setSelectedId] = useState(null);
  const selectRef = useRef(null);     // animation intent bus (see SceneContents)

  const resetCamera = () => {
    setSelectedId(null);
    selectRef.current = { type: "reset", at: performance.now() };
    if (onLocationChange) onLocationChange(null);
    const c = controlsRef.current;
    if (c) c.reset();
  };

  const handleSelect = useCallback((loc) => {
    setSelectedId(loc.id);
    selectRef.current = { type: "focus", loc, at: performance.now() };
    if (onLocationChange) onLocationChange(loc);
  }, [onLocationChange]);

  return (
    <div
      className={`earth-canvas-wrap ${className}`}
      onDoubleClick={resetCamera}
      data-testid="earth-3d-canvas"
    >
      <Canvas
        dpr={[1, 2]}
        camera={{ position: [0, 0.35, 3.6], fov: 42, near: 0.1, far: 100 }}
        gl={{
          antialias: true,
          powerPreference: "high-performance",
          alpha: true,
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1.05,
        }}
        style={{ background: "transparent" }}
      >
        {/* Lighting — PURPLE + PINK ONLY. Zero blue. */}
        <ambientLight intensity={0.28} color={HERO_COLORS.purple} />
        <directionalLight position={[5, 3, 5]} intensity={2.4} color="#ffffff" />
        <pointLight position={[-4, -2, -3]} intensity={2.0} color={HERO_COLORS.purple} distance={20} />
        <pointLight position={[4, 3, -4]}   intensity={1.6} color={HERO_COLORS.pink}   distance={20} />
        <pointLight position={[0, -4, 2]}   intensity={1.1} color={HERO_COLORS.glow}   distance={15} />

        <Suspense fallback={null}>
          <ParallaxRig>
            <FloatingGroup>
              <SceneContents
                selectRef={selectRef}
                controlsRef={controlsRef}
                selectedId={selectedId}
                onSelect={handleSelect}
              />
            </FloatingGroup>
            <Particles count={1400} />
          </ParallaxRig>
          <Preload all />
        </Suspense>

        <OrbitControls
          ref={controlsRef}
          enablePan={false}
          enableZoom={true}
          minDistance={2.2}
          maxDistance={6.5}
          zoomSpeed={0.55}
          rotateSpeed={0.55}
          enableDamping
          dampingFactor={0.08}
          autoRotate
          autoRotateSpeed={0.35}
          makeDefault
        />
      </Canvas>
    </div>
  );
}

/* -------------------------------------------------------------------------
 *  SceneContents — lives inside the Canvas so it can call useFrame / useThree.
 *  Owns the earthGroup ref that carries Earth + Markers + Arcs together.
 *  Applies:
 *    - slow continuous auto-spin on the group
 *    - quaternion-slerp focus animation on click
 *    - subtle camera dolly-in during focus
 *    - autoRotate suspend + resume across the 5s hold window
 * ----------------------------------------------------------------------- */
function SceneContents({ selectRef, controlsRef, selectedId, onSelect }) {
  const groupRef = useRef();
  const focusRef = useRef(null);          // { fromQuat, toQuat, t0, duration, camFrom, camTo }
  const suspendUntil = useRef(0);         // performance.now() timestamp
  const { camera } = useThree();
  const initialCamPos = useRef(null);

  useEffect(() => {
    if (!initialCamPos.current) initialCamPos.current = camera.position.clone();
  }, [camera]);

  useFrame((_, delta) => {
    const g = groupRef.current;
    if (!g) return;
    const now = performance.now();

    /* 1. React to new selection intents ------------------------------------ */
    const intent = selectRef.current;
    if (intent) {
      selectRef.current = null;
      if (intent.type === "focus") {
        const { loc } = intent;
        const local = new THREE.Vector3(...latLngToVec3(loc.lat, loc.lng, 1));
        // Target world direction: point on unit sphere in the direction of the camera.
        const camDir = camera.position.clone().normalize();
        // Slight upward bias so labels don't collide with hero title.
        camDir.y += 0.05;
        camDir.normalize();
        const toQuat = new THREE.Quaternion().setFromUnitVectors(local, camDir);

        // Camera dolly target — pull ~14% closer along its current direction.
        const camFrom = camera.position.clone();
        const camTo = camFrom.clone().multiplyScalar(DOLLY_IN_FACTOR);

        focusRef.current = {
          fromQuat: g.quaternion.clone(),
          toQuat,
          t0: now,
          duration: FOCUS_DURATION_MS,
          camFrom, camTo,
        };
        // Freeze auto-rotate for the whole focus + hold window
        suspendUntil.current = now + FOCUS_DURATION_MS + PAUSE_AFTER_MS;
        if (controlsRef.current) controlsRef.current.autoRotate = false;
      } else if (intent.type === "reset") {
        const camFrom = camera.position.clone();
        const camTo = initialCamPos.current ? initialCamPos.current.clone() : camFrom;
        focusRef.current = {
          fromQuat: g.quaternion.clone(),
          toQuat: new THREE.Quaternion(),
          t0: now,
          duration: FOCUS_DURATION_MS,
          camFrom, camTo,
        };
        suspendUntil.current = now + FOCUS_DURATION_MS;
        if (controlsRef.current) controlsRef.current.autoRotate = false;
      }
    }

    /* 2. Animate focus if active ------------------------------------------- */
    const focus = focusRef.current;
    if (focus) {
      const t = Math.min(1, (now - focus.t0) / focus.duration);
      const e = easeInOutCubic(t);
      g.quaternion.slerpQuaternions(focus.fromQuat, focus.toQuat, e);
      // Camera dolly (only if OrbitControls user hasn't taken over — check by comparing distance)
      if (focus.camFrom && focus.camTo) {
        const nx = focus.camFrom.x + (focus.camTo.x - focus.camFrom.x) * e;
        const ny = focus.camFrom.y + (focus.camTo.y - focus.camFrom.y) * e;
        const nz = focus.camFrom.z + (focus.camTo.z - focus.camFrom.z) * e;
        camera.position.set(nx, ny, nz);
        if (controlsRef.current) controlsRef.current.update();
      }
      if (t >= 1) focusRef.current = null;
      return; // don't add auto-spin on the same frame
    }

    /* 3. Auto-spin if not suspended ---------------------------------------- */
    if (now >= suspendUntil.current) {
      g.rotation.y += delta * 0.045;
      if (controlsRef.current && !controlsRef.current.autoRotate) {
        controlsRef.current.autoRotate = true;   // resume cinematic camera orbit
      }
    }
  });

  return (
    <group ref={groupRef}>
      <ScaleCompensator>
        <EarthMesh radius={1} />
        <LocationMarkers radius={1} selectedId={selectedId} onSelect={onSelect} />
        <NetworkArcs radius={1.005} />
        <Atmosphere radius={1.18} intensity={1.35} />
        <Atmosphere radius={1.35} intensity={0.35} />
        <OrbitRings />
      </ScaleCompensator>
    </group>
  );
}

/* -------------------------------------------------------------------------
 *  ScaleCompensator — canvas grew (see .earth-canvas-bleed CSS) so we scale
 *  the whole earth-group inversely to keep the visible earth size identical
 *  to the pre-bleed layout on every breakpoint.
 *  Baseline heights match the .earth-canvas-bleed breakpoints:
 *    mobile  container ≈ 420px, bleed ±60px  →  actual canvas ≈ 540px
 *    sm      container ≈ 520px, bleed ±90px  →  actual canvas ≈ 700px
 *    lg      container ≈ 640px, bleed ±120px →  actual canvas ≈ 880px
 * ----------------------------------------------------------------------- */
function ScaleCompensator({ children }) {
  const { size } = useThree();
  // Choose the design baseline height for the current viewport range and
  // compute the inverse scale needed to preserve the earth's rendered size.
  let baseline = 420;
  if (size.height >= 620) baseline = 520;
  if (size.height >= 800) baseline = 640;
  const scale = Math.max(0.55, Math.min(1, baseline / size.height));
  return <group scale={scale}>{children}</group>;
}

/* -------------------------------------------------------------------------
 *  ParallaxRig — pushes the whole scene by ~mouse position for depth cue.
 * ----------------------------------------------------------------------- */
function ParallaxRig({ children }) {
  const mouse = useEarthAnimation();
  const ref   = useRef();

  useFrame(() => {
    if (!ref.current) return;
    const tx = mouse.current.x * 0.10;
    const ty = -mouse.current.y * 0.08;
    ref.current.position.x += (tx - ref.current.position.x) * 0.05;
    ref.current.position.y += (ty - ref.current.position.y) * 0.05;
  });

  return <group ref={ref}>{children}</group>;
}

/* -------------------------------------------------------------------------
 *  FloatingGroup — extremely gentle up-and-down float to feel "alive".
 * ----------------------------------------------------------------------- */
function FloatingGroup({ children }) {
  const ref = useRef();
  useFrame(({ clock }) => {
    if (!ref.current) return;
    const t = clock.getElapsedTime();
    ref.current.position.y = Math.sin(t * 0.5) * 0.05;
    ref.current.rotation.z = Math.sin(t * 0.3) * 0.015;
  });
  return <group ref={ref}>{children}</group>;
}
